import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  TablePagination,
  CircularProgress,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddLinkIcon from "@mui/icons-material/AddLink";
import SaveIcon from "@mui/icons-material/Save";
import SmsIcon from "@mui/icons-material/Sms";
import ForumIcon from "@mui/icons-material/Forum";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  useSmsdlrurlgetQuery,
  useSmsdlrurlpostMutation,
  useSmsdlrurlputMutation,
  useSmsdlrurldltMutation,
} from "../store/services/smsApi.js";
import {
  useGetrcsdlrurlQuery,
  usePostrcsdlrurlMutation,
  usePutrcsdlrurlMutation,
  useDltrcsdlrurlMutation,
} from "../store/services/rcstemplateApi.js";

import {
  useGetwhatsappdlrurlQuery,
  usePatchwhatsappdlrurlMutation,
} from "../store/services/whatsappApi.js";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

const TABS = [
  { label: "SMS", icon: SmsIcon },
  { label: "RCS", icon: ForumIcon },
  { label: "WhatsApp", icon: WhatsAppIcon },
  { label: "Email", icon: MailOutlineIcon },
];

const cellSX = {
  p: "8px",
  fontSize: "0.75rem",
  borderRight: "0.5px solid #e0e0e0",
};

function Dlrurl() {
  const [activeTab, setActiveTab] = useState(0);

  // form state
  const [smsurl, setSmsurl] = useState("");
  const [rcsurl, setRcsurl] = useState("");
  const [whatsurl, setWhatsappurl] = useState("");
  const [emailsurl, setEmailsurl] = useState("");
  const [isDlrUrlForAPI, setIsDlrUrlForAPI] = useState(false);
  const [both, setBoth] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [customer_id, setCustomer_id] = useState("");

  // whatsapp-specific state
  const [whatsEditing, setWhatsEditing] = useState(false);
  const [whatsApiFlag, setWhatsApiFlag] = useState(false);
  const [whatsBulkFlag, setWhatsBulkFlag] = useState(false);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Get customer_id from token
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Access token not found");
        return;
      }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) {
        toast.error("Customer ID not found in token");
      } else {
        setCustomer_id(id);
      }
    } catch (err) {
      toast.error("Invalid access token format: " + err.message);
    }
  }, []);

  // ── WhatsApp ──
  const {
    data: whatsdlrurl,
    isLoading: whatsdlrLoading,
    isError: whatsgeterror,
  } = useGetwhatsappdlrurlQuery(customer_id, { skip: !customer_id });

  const [patchWhatsapp, { isLoading: whatsPatchLoading }] =
    usePatchwhatsappdlrurlMutation();

  // Pre-fill WhatsApp form when data loads
  useEffect(() => {
    if (whatsdlrurl?.data) {
      setWhatsappurl(whatsdlrurl.data.customer_webhook || "");
      setWhatsApiFlag(whatsdlrurl.data.attributes?.api || false);
      setWhatsBulkFlag(whatsdlrurl.data.attributes?.bulk || false);
    }
  }, [whatsdlrurl]);

  const handleWhatsappSubmit = async () => {
    if (!whatsurl.trim()) {
      setErrorMessage("Please enter WhatsApp URL");
      return;
    }
    try {
      await patchWhatsapp({
        customer_id,
        customer_webhook: whatsurl,
        attributes: { api: whatsApiFlag, bulk: whatsBulkFlag },
      }).unwrap();
      setSuccessMessage("WhatsApp URL updated successfully!");
      setWhatsEditing(false);
    } catch (error) {
      console.error("WhatsApp submit failed:", error);
      setErrorMessage(error.data?.message || "Failed to save WhatsApp URL");
    }
  };

  // ── SMS ──
  const {
    data: smsget,
    isLoading: smsGetLoading,
    isError: smsGetError,
    refetch: refetchSms,
  } = useSmsdlrurlgetQuery();

  const [smsdlrpost, { isLoading: smsPostLoading }] =
    useSmsdlrurlpostMutation();
  const [smsdlrput, { isLoading: smsPutLoading }] = useSmsdlrurlputMutation();
  const [smsdlrdlt, { isLoading: smsDltLoading }] = useSmsdlrurldltMutation();
  const smsRows = smsget?.data || [];

  // ── RCS ──
  const {
    data: rcsget,
    isLoading: rcsGetLoading,
    isError: rcsGetError,
    refetch: refetchRcs,
  } = useGetrcsdlrurlQuery({
    page: page + 1,
    limit: rowsPerPage,
  });

  const [rcsdlrpost, { isLoading: rcsPostLoading }] =
    usePostrcsdlrurlMutation();
  const [rcsdlrput, { isLoading: rcsPutLoading }] = usePutrcsdlrurlMutation();
  const [rcsdlrdlt, { isLoading: rcsDltLoading }] = useDltrcsdlrurlMutation();
  const rcsRows = rcsget?.data || [];

  // Auto-dismiss messages after 3 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const currentRows =
    activeTab === 0 ? smsRows : activeTab === 1 ? rcsRows : [];

  // No search/filter anymore — rows are shown as-is
  const filteredRows = currentRows;

  const paginatedRows = useMemo(() => {
    if (activeTab === 1) return filteredRows;
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage, activeTab]);

  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  const handleChangePage = (e, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    if (value === "isDlrUrlForAPI") {
      setIsDlrUrlForAPI(true);
      setBoth(false);
    } else if (value === "both") {
      setBoth(true);
      setIsDlrUrlForAPI(false);
    }
  };

  const resetForm = () => {
    setSmsurl("");
    setRcsurl("");
    setIsDlrUrlForAPI(false);
    setBoth(false);
    setEditingId(null);
  };

  const handleSmsSubmit = async () => {
    if (!smsurl.trim()) {
      setErrorMessage("Please enter SMS URL");
      return;
    }

    try {
      const payload = {
        DlrUrl: smsurl,
        IsDlrUrlForAPI: isDlrUrlForAPI,
        Both: both,
      };

      if (editingId) {
        await smsdlrput({
          customerDlrUrl_id: editingId,
          smspayload: payload,
        }).unwrap();
        setSuccessMessage("SMS URL updated successfully!");
      } else {
        await smsdlrpost(payload).unwrap();
        setSuccessMessage("SMS URL added successfully!");
      }

      resetForm();
      refetchSms();
    } catch (error) {
      console.error("SMS submit failed:", error);
      setErrorMessage(error.data?.message || "Failed to save SMS URL");
    }
  };

  const handleRcsSubmit = async () => {
    if (!rcsurl.trim()) {
      setErrorMessage("Please enter RCS URL");
      return;
    }

    try {
      const rcspayload = {
        DlrUrl: rcsurl,
        IsDlrUrlForAPI: isDlrUrlForAPI,
        Both: both,
      };

      if (editingId) {
        await rcsdlrput({
          rcspayload,
          customerDlrUrl_id: editingId,
        }).unwrap();
        setSuccessMessage("RCS URL updated successfully!");
      } else {
        await rcsdlrpost({ rcspayload }).unwrap();
        setSuccessMessage("RCS URL added successfully!");
      }

      resetForm();
      refetchRcs();
    } catch (error) {
      console.error("RCS submit failed:", error);
      setErrorMessage(error.data?.message || "Failed to save RCS URL");
    }
  };

  const handleSmsDelete = async (customerDlrUrl_id) => {
    if (!window.confirm("Delete this DLR URL?")) return;
    try {
      await smsdlrdlt(customerDlrUrl_id).unwrap();
      setSuccessMessage("SMS URL deleted successfully!");
      refetchSms();
    } catch (error) {
      console.error("Failed to delete SMS DLR URL:", error);
      setErrorMessage("Failed to delete SMS DLR URL");
    }
  };

  const handleRcsDelete = async (customerDlrUrl_id) => {
    if (!window.confirm("Delete this DLR URL?")) return;
    try {
      await rcsdlrdlt({ customerDlrUrl_id }).unwrap();
      setSuccessMessage("RCS URL deleted successfully!");
      refetchRcs();
    } catch (error) {
      console.error("Failed to delete RCS DLR URL:", error);
      setErrorMessage("Failed to delete RCS DLR URL");
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.customerDlrUrl_id);
    setIsDlrUrlForAPI(row.IsDlrUrlForAPI);
    setBoth(row.Both);

    if (activeTab === 0) {
      setSmsurl(row.DlrUrl);
      setRcsurl("");
    } else if (activeTab === 1) {
      setRcsurl(row.DlrUrl);
      setSmsurl("");
    }
  };

  const renderPill = (value) => (
    <span
      style={{
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "0.7rem",
        fontWeight: 600,
        backgroundColor: value ? "#e6f7ed" : "#eceef0",
        color: value ? "#1e7e42" : "#5f6b76",
      }}
    >
      {value ? "Yes" : "No"}
    </span>
  );

  // Shared "add / update" form card for SMS and RCS — only the fields
  // relevant to the active tab render, instead of both URL inputs and
  // both submit buttons showing at once.
  const renderUrlForm = ({
    label,
    value,
    onChange,
    onSubmit,
    submitting,
    icon: Icon,
  }) => (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        alignItems: "center",
        p: 2,
        borderRadius: "10px",
        mb: 2.5,
        borderColor: "#dde8ef",
        bgcolor: "#fbfdff",
      }}
    >
      <Icon sx={{ color: "#0288d1", fontSize: 22, mr: -0.5 }} />
      <TextField
        size="small"
        value={value}
        label={label}
        onChange={onChange}
        sx={{ minWidth: 260, flex: 1 }}
      />

      <FormControl size="small">
        <RadioGroup
          row
          value={isDlrUrlForAPI ? "isDlrUrlForAPI" : both ? "both" : ""}
          onChange={handleChange}
        >
          <FormControlLabel
            value="isDlrUrlForAPI"
            control={<Radio size="small" />}
            label="For API"
          />
          <FormControlLabel
            value="both"
            control={<Radio size="small" />}
            label="Both"
          />
        </RadioGroup>
      </FormControl>

      <Button
        variant="contained"
        size="small"
        startIcon={
          submitting ? null : editingId ? (
            <SaveIcon fontSize="small" />
          ) : (
            <AddLinkIcon fontSize="small" />
          )
        }
        sx={{
          textTransform: "none",
          fontSize: 13,
          borderRadius: 1.5,
          px: 2.5,
          whiteSpace: "nowrap",
        }}
        onClick={onSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <CircularProgress size={18} color="inherit" />
        ) : editingId ? (
          `Update ${label.replace(" URL", "")}`
        ) : (
          `Add ${label.replace(" URL", "")}`
        )}
      </Button>

      {editingId && (
        <Button
          variant="text"
          size="small"
          onClick={resetForm}
          sx={{ textTransform: "none", fontSize: 13 }}
        >
          Cancel
        </Button>
      )}
    </Paper>
  );

  const renderTable = ({
    loading,
    error,
    onDelete,
    onEdit,
    dltLoading,
    emptyLabel,
  }) => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: "10px", overflow: "hidden" }}
    >
      <Table stickyHeader>
        <TableHead
          sx={{
            "& th": {
              bgcolor: "#0288d1",
              color: "#fff",
              fontSize: "0.8rem",
              p: "8px",
              borderRight: "0.5px solid #1b1b1b",
            },
          }}
        >
          <TableRow>
            {[
              "Customer ID",
              "DLR URL",
              "For API",
              "Both",
              "Created At",
              "Updated At",
              "Action",
            ].map((h) => (
              <TableCell key={h}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <CircularProgress size={22} />
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell
                colSpan={7}
                align="center"
                sx={{ color: "error.main" }}
              >
                Error loading DLR URLs
              </TableCell>
            </TableRow>
          ) : paginatedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={7}
                align="center"
                sx={{ py: 4, color: "text.secondary" }}
              >
                {emptyLabel}
              </TableCell>
            </TableRow>
          ) : (
            paginatedRows.map((row) => (
              <TableRow
                key={row.customerDlrUrl_id}
                hover
                sx={{ "& td": { borderBottom: "0.5px solid #e0e0e0" } }}
              >
                <TableCell sx={cellSX}>{row.Customer_id}</TableCell>
                <TableCell sx={cellSX}>
                  {editingId === row.customerDlrUrl_id ? (
                    <TextField
                      size="small"
                      value={activeTab === 0 ? smsurl : rcsurl}
                      onChange={(e) =>
                        activeTab === 0
                          ? setSmsurl(e.target.value)
                          : setRcsurl(e.target.value)
                      }
                      sx={{ fontSize: "0.75rem", minWidth: "150px" }}
                    />
                  ) : (
                    row.DlrUrl
                  )}
                </TableCell>
                <TableCell sx={cellSX}>
                  {renderPill(row.IsDlrUrlForAPI)}
                </TableCell>
                <TableCell sx={cellSX}>{renderPill(row.Both)}</TableCell>
                <TableCell sx={cellSX}>
                  {row.createdat
                    ? new Date(row.createdat).toLocaleString()
                    : "N/A"}
                </TableCell>
                <TableCell sx={cellSX}>
                  {row.updatedat
                    ? new Date(row.updatedat).toLocaleString()
                    : "N/A"}
                </TableCell>
                <TableCell sx={cellSX}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {editingId === row.customerDlrUrl_id ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            activeTab === 0
                              ? handleSmsSubmit()
                              : handleRcsSubmit()
                          }
                          disabled={
                            activeTab === 0 ? smsPutLoading : rcsPutLoading
                          }
                          sx={{ fontSize: "0.7rem", px: 1 }}
                        >
                          {(activeTab === 0 ? smsPutLoading : rcsPutLoading) ? (
                            <CircularProgress size={14} />
                          ) : (
                            "Save"
                          )}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={resetForm}
                          sx={{ fontSize: "0.7rem", px: 1 }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => onEdit(row)}
                          sx={{ fontSize: "0.7rem", px: 1 }}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={dltLoading}
                          onClick={() => onDelete(row.customerDlrUrl_id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!loading && !error && filteredRows.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={
            activeTab === 1
              ? (rcsget?.pagination?.totalRecords ?? filteredRows.length)
              : filteredRows.length
          }
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-toolbar": {
              fontSize: "0.75rem",
              minHeight: "48px",
            },
            "& .MuiTablePagination-selectLabel": { fontSize: "0.75rem" },
            "& .MuiTablePagination-displayedRows": { fontSize: "0.75rem" },
            "& .MuiTablePagination-select": { fontSize: "0.75rem" },
          }}
        />
      )}
    </TableContainer>
  );

  const renderWhatsappTable = () => {
    const row = whatsdlrurl?.data;

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "10px", overflow: "hidden" }}
      >
        <Table stickyHeader>
          <TableHead
            sx={{
              "& th": {
                bgcolor: "#0288d1",
                color: "#fff",
                fontSize: "0.8rem",
                p: "8px",
                borderRight: "0.5px solid #1b1b1b",
              },
            }}
          >
            <TableRow>
              {[
                "Customer ID",
                "Webhook URL",
                "API",
                "Bulk",
                "Created At",
                "Updated At",
                "Action",
              ].map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {whatsdlrLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : whatsgeterror ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ color: "error.main" }}
                >
                  Error loading WhatsApp DLR URL
                </TableCell>
              </TableRow>
            ) : !row ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  No WhatsApp DLR URL found
                </TableCell>
              </TableRow>
            ) : (
              <TableRow
                hover
                sx={{ "& td": { borderBottom: "0.5px solid #e0e0e0" } }}
              >
                <TableCell sx={cellSX}>{row.customer_id}</TableCell>
                <TableCell sx={cellSX}>
                  {whatsEditing ? (
                    <TextField
                      size="small"
                      value={whatsurl}
                      onChange={(e) => setWhatsappurl(e.target.value)}
                      sx={{ fontSize: "0.75rem", minWidth: "200px" }}
                    />
                  ) : (
                    row.customer_webhook || "Not configured"
                  )}
                </TableCell>
                <TableCell sx={cellSX}>
                  {whatsEditing ? (
                    <Checkbox
                      size="small"
                      checked={whatsApiFlag}
                      onChange={(e) => setWhatsApiFlag(e.target.checked)}
                    />
                  ) : (
                    renderPill(row.attributes?.api)
                  )}
                </TableCell>
                <TableCell sx={cellSX}>
                  {whatsEditing ? (
                    <Checkbox
                      size="small"
                      checked={whatsBulkFlag}
                      onChange={(e) => setWhatsBulkFlag(e.target.checked)}
                    />
                  ) : (
                    renderPill(row.attributes?.bulk)
                  )}
                </TableCell>
                <TableCell sx={cellSX}>
                  {row.created_at
                    ? new Date(row.created_at).toLocaleString()
                    : "N/A"}
                </TableCell>
                <TableCell sx={cellSX}>
                  {row.updated_at
                    ? new Date(row.updated_at).toLocaleString()
                    : "N/A"}
                </TableCell>
                <TableCell sx={cellSX}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {whatsEditing ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={handleWhatsappSubmit}
                          disabled={whatsPatchLoading}
                          sx={{ fontSize: "0.7rem", px: 1 }}
                        >
                          {whatsPatchLoading ? (
                            <CircularProgress size={14} />
                          ) : (
                            "Save"
                          )}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setWhatsEditing(false)}
                          sx={{ fontSize: "0.7rem", px: 1 }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => setWhatsEditing(true)}
                        sx={{ fontSize: "0.7rem", px: 1 }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ maxWidth: "100%", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#1a2733" }}
        >
          DLR URL Management
        </Typography>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: "8px" }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: "8px" }}
          onClose={() => setErrorMessage("")}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Tab switcher — pill-style segmented control, each with its own icon */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          p: 0.75,
          mb: 3,
          borderRadius: "12px",
          bgcolor: "#eef3f7",
          width: "fit-content",
        }}
      >
        {TABS.map(({ label, icon: Icon }, index) => {
          const isActive = activeTab === index;
          return (
            <Box
              key={label}
              onClick={() => {
                setActiveTab(index);
                resetForm();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 2.5,
                py: 1,
                borderRadius: "9px",
                fontSize: "0.85rem",
                fontWeight: isActive ? 600 : 500,
                color: isActive ? "#fff" : "#4a5a68",
                bgcolor: isActive ? "#0288d1" : "transparent",
                cursor: "pointer",
                transition: "background-color 0.15s ease, color 0.15s ease",
                userSelect: "none",
                "&:hover": {
                  bgcolor: isActive ? "#0288d1" : "#dde8ef",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
              {label}
            </Box>
          );
        })}
      </Box>

      {/* Only the active tab's own form renders — SMS shows just the SMS
          field + Add SMS button, RCS shows just its own, and so on. */}
      {activeTab === 0 &&
        renderUrlForm({
          label: "SMS URL",
          value: smsurl,
          onChange: (e) => setSmsurl(e.target.value),
          onSubmit: handleSmsSubmit,
          submitting: smsPostLoading || smsPutLoading,
          icon: SmsIcon,
        })}

      {activeTab === 1 &&
        renderUrlForm({
          label: "RCS URL",
          value: rcsurl,
          onChange: (e) => setRcsurl(e.target.value),
          onSubmit: handleRcsSubmit,
          submitting: rcsPostLoading || rcsPutLoading,
          icon: ForumIcon,
        })}

      {activeTab === 0 &&
        renderTable({
          loading: smsGetLoading,
          error: smsGetError,
          onDelete: handleSmsDelete,
          onEdit: handleEdit,
          dltLoading: smsDltLoading,
          emptyLabel: "No SMS DLR URLs found",
        })}

      {activeTab === 1 &&
        renderTable({
          loading: rcsGetLoading,
          error: rcsGetError,
          onDelete: handleRcsDelete,
          onEdit: handleEdit,
          dltLoading: rcsDltLoading,
          emptyLabel: "No RCS DLR URLs found",
        })}

      {activeTab === 2 && renderWhatsappTable()}

      {activeTab === 3 && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <MailOutlineIcon sx={{ fontSize: 40, color: "#c6d2db", mb: 1 }} />
          <Typography color="text.secondary">
            Email DLR URL table coming soon.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default Dlrurl;
